/**
 * Page context — the small text block that rides along on every chat
 * message so the agent knows where the user is and what they're looking
 * at. Two layers:
 *
 *   L1 (always):  [User is viewing: <Label> (<path>)]
 *   L2 (if registered):  [Page state: ...key/value bullet list...]
 *
 * L2 providers register snapshot fns via useRegisterPageContext (see
 * src/contexts/page-context-registry.tsx). If a route hasn't registered
 * one, the agent gets just L1 — same as the pre-2026-05-03 behavior.
 *
 * Spec: docs/superpowers/specs/2026-05-03-page-context-l2-design.md
 */

const PAGE_LABELS: Record<string, string> = {
  '/chat': 'Main Chat',
  '/contacts': 'CRM Contacts',
  '/calendar': 'Calendar',
  '/tasks': 'Tasks',
  '/settings': 'Settings',
  '/marketplace': 'Marketplace',
  '/projects': 'Projects',
  '/analytics': 'Analytics',
  '/docs': 'Documents',
  '/agents': 'Agent Directory',
  '/console': 'Console',
  '/rooms': 'Rooms',
};

// ── L2 types ────────────────────────────────────────────────────────────────

export type PageSnapshotPrimitive = string | number | boolean | null | undefined;
export type PageSnapshotValue = PageSnapshotPrimitive | PageSnapshotPrimitive[] | PageSnapshot[];
export type PageSnapshot = Record<string, PageSnapshotValue>;
export type PageContextProvider = () => PageSnapshot;

const MAX_TOTAL_CHARS = 2000; // matches workspace-main pageContext cap
/** Appended when the composed block exceeds MAX_TOTAL_CHARS. Its length is
 *  subtracted from the slice so output is never longer than the cap — the
 *  gateway slices inbound pageContext to exactly MAX_TOTAL_CHARS, and one
 *  character of overflow shears the block's terminating bracket. */
const TRUNCATION_SUFFIX = '... [truncated]';
const MAX_VALUE_CHARS = 80;
const MAX_ARRAY_ITEMS = 5;

// ── L1 label builder ────────────────────────────────────────────────────────

function buildLabel(pathname: string): string {
  if (PAGE_LABELS[pathname]) {
    return `[User is viewing: ${PAGE_LABELS[pathname]} (${pathname})]`;
  }
  for (const [route, label] of Object.entries(PAGE_LABELS)) {
    if (pathname.startsWith(route + '/')) {
      const sub = pathname.slice(route.length + 1);
      return `[User is viewing: ${label} — ${sub} (${pathname})]`;
    }
  }
  const segment = pathname.split('/').filter(Boolean).pop() || 'Home';
  const label = segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
  return `[User is viewing: ${label} (${pathname})]`;
}

// ── L2 snapshot serializer ──────────────────────────────────────────────────

function truncateValue(v: unknown): string {
  if (v === null || v === undefined) return 'null';
  const s = typeof v === 'string' ? v : JSON.stringify(v);
  if (s.length <= MAX_VALUE_CHARS) return s;
  return `${s.slice(0, MAX_VALUE_CHARS - 3)}...`;
}

function serializeSnapshot(snapshot: PageSnapshot): string {
  const lines: string[] = ['[Page state:'];
  for (const [key, value] of Object.entries(snapshot)) {
    if (Array.isArray(value)) {
      lines.push(`- ${key}:`);
      const items = value.slice(0, MAX_ARRAY_ITEMS);
      for (const item of items) {
        if (item && typeof item === 'object') {
          // Render object as "key: value, key: value" per item.
          const inner = Object.entries(item)
            .map(([k, v]) => `${k}: ${truncateValue(v)}`)
            .join(', ');
          lines.push(`  - ${truncateValue(inner)}`);
        } else {
          lines.push(`  - ${truncateValue(item)}`);
        }
      }
      if (value.length > MAX_ARRAY_ITEMS) {
        lines.push(`  - ... (${value.length - MAX_ARRAY_ITEMS} more)`);
      }
    } else {
      lines.push(`- ${key}: ${truncateValue(value)}`);
    }
  }
  lines[lines.length - 1] += ']';
  return lines.join('\n');
}

// ── Project context (L0 — prepends label & snapshot) ───────────────────────

export interface ActiveProjectRef {
  id: string;
  name: string;
  description?: string | null;
}

function buildProjectContext(project: ActiveProjectRef | null | undefined): string | null {
  if (!project || !project.id) return null;
  const desc = project.description ? ` — ${project.description}` : '';
  return `[Active project: ${project.name}${desc}]`;
}

// ── Public API ──────────────────────────────────────────────────────────────

/**
 * Build the full page context string for a chat send.
 *
 * @param pathname Current router pathname.
 * @param snapshot Optional L2 snapshot from the registered provider.
 *                 Pass null/undefined to skip L2 (label-only output).
 * @param activeProject Optional active project from ProjectProvider; gets
 *                      prepended as a first-class L0 line so every agent
 *                      knows which project the user is working on.
 */
export function getPageContext(
  pathname: string,
  snapshot?: PageSnapshot | null,
  activeProject?: ActiveProjectRef | null,
): string {
  const projectLine = buildProjectContext(activeProject);
  const label = buildLabel(pathname);
  const head = projectLine ? `${projectLine}\n${label}` : label;

  if (!snapshot || Object.keys(snapshot).length === 0) {
    return head;
  }
  const stateBlock = serializeSnapshot(snapshot);
  const combined = `${head}\n${stateBlock}`;
  if (combined.length <= MAX_TOTAL_CHARS) return combined;
  // TRUNCATION_SUFFIX is 15 chars, not 14. The old `- 14` produced a 2001-char
  // string, and the gateway slices inbound pageContext to 2000 — shearing the
  // final `]` and writing a block that no read-time parser can recognise as
  // terminated. That is how unterminated context blocks entered the database.
  return `${combined.slice(0, MAX_TOTAL_CHARS - TRUNCATION_SUFFIX.length)}${TRUNCATION_SUFFIX}`;
}
